import tutorial1 from "app/assets/pixellence-tut-1.gif";
import tutorial2 from "app/assets/pixellence-tut-2.gif";
import tutorial3 from "app/assets/pixellence-tut-3.gif";
import tutorial4 from "app/assets/pixellence-tut-4.gif";
import tutorial5 from "app/assets/pixellence-tut-5.gif";
import tutorial6_1 from "app/assets/pixellence-tut-6-1.gif";
import tutorial6_2 from "app/assets/pixellence-tut-6-2.gif";
import tutorial6_3 from "app/assets/pixellence-tut-6-3.gif";
import tutorial7_1 from "app/assets/pixellence-tut-7-1.gif";
import tutorial7_2 from "app/assets/pixellence-tut-7-2.gif";
import tutorial7_3 from "app/assets/pixellence-tut-7-3.gif";
import cn from "app/util/classnames";

const DEFAULT_IMG_WIDTH = 315;
const DEFAULT_IMG_HEIGHT = 290;
const DEFAULT_IMG_CLASS = "mx-auto rounded-md shadow-md";

interface TutorialPage {
  ImgContent: React.ReactNode;
  TextContent: React.ReactNode;
}

export const HOW_TO_PLAY_PAGES: TutorialPage[] = [
  {
    ImgContent: (
      <img
        className={DEFAULT_IMG_CLASS}
        width={DEFAULT_IMG_WIDTH}
        height={DEFAULT_IMG_HEIGHT}
        src={tutorial1}
        alt="A demonstration of Pixellence gameplay"
      />
    ),
    TextContent: (
      <div className="flex flex-col gap-3">
        <p>
          Pixellence is a game where you use numerical clues to color squares in
          a grid, leading to a picture at the end.{" "}
        </p>
        <p>
          <strong>
            The numbers beside a row or column tell you how many squares in that
            row/column must be colored in
          </strong>
          .
        </p>
      </div>
    ),
  },
  {
    ImgContent: (
      <img
        className={DEFAULT_IMG_CLASS}
        width={DEFAULT_IMG_WIDTH}
        height={DEFAULT_IMG_HEIGHT}
        src={tutorial2}
        alt="A demonstration of Pixellence gameplay"
      />
    ),
    TextContent: (
      <p>
        For example: for each of these columns, the "5" clue tells us that the
        column contains five colored-in squares.
      </p>
    ),
  },
  {
    ImgContent: (
      <img
        className={DEFAULT_IMG_CLASS}
        width={DEFAULT_IMG_WIDTH}
        height={DEFAULT_IMG_HEIGHT}
        src={tutorial3}
        alt="A demonstration of Pixellence gameplay"
      />
    ),
    TextContent: (
      <div className="flex flex-col gap-3">
        <p>
          "0" clues indicate that there are no colored-in squares in that row or
          column.
        </p>
        <p>
          <strong>You can switch between coloring and X-ing out</strong> by
          tapping the paintbrush and X buttons, or by pressing space if you're
          on a computer. Right-clicking also places X's.
        </p>
      </div>
    ),
  },
  {
    ImgContent: (
      <img
        className={DEFAULT_IMG_CLASS}
        width={DEFAULT_IMG_WIDTH}
        height={DEFAULT_IMG_HEIGHT}
        src={tutorial4}
        alt="A demonstration of Pixellence gameplay"
      />
    ),
    TextContent: (
      <p>
        <strong>Clues with multiple numbers</strong> indicate groups of
        colored-in squares separated by one or more white squares. The groups
        are always in the order given; in this example the group of 3 must be to
        the left of the single colored-in square.
      </p>
    ),
  },
  {
    ImgContent: (
      <img
        className={DEFAULT_IMG_CLASS}
        width={DEFAULT_IMG_WIDTH}
        height={DEFAULT_IMG_HEIGHT}
        src={tutorial5}
        alt="A demonstration of Pixellence gameplay"
      />
    ),
    TextContent: (
      <p>
        <strong>You never need to guess</strong>; you can always complete the
        puzzle using logic and the process of elimination alone.
      </p>
    ),
  },
  {
    ImgContent: (
      <div className="flex flex-col items-center gap-5 mt-3 mb-5">
        <img
          className={cn("max-w-60", DEFAULT_IMG_CLASS)}
          src={tutorial6_1}
          width={327}
          height={54}
        />
        <img
          className={cn("max-w-60", DEFAULT_IMG_CLASS)}
          src={tutorial6_2}
          width={327}
          height={54}
        />
        <img
          className={cn("max-w-60", DEFAULT_IMG_CLASS)}
          src={tutorial6_3}
          width={327}
          height={54}
        />
      </div>
    ),
    TextContent: (
      <div className="flex flex-col gap-3">
        <p>
          <strong>One more essential technique:</strong> many levels won't have
          entire rows you can fill in to start, but you can still make progress.
        </p>
        <p>
          In this example, the 4 colored-in squares could start on the left or
          the right, meaning you can't fill them all in just yet. But either
          way, <strong>the middle 3 squares would be filled in</strong>. So for
          now you can color those in.
        </p>
      </div>
    ),
  },
  {
    ImgContent: (
      <div className="flex flex-col items-center gap-5 mt-3 mb-5 w-fit relative">
        <img
          className={cn("max-w-67", DEFAULT_IMG_CLASS)}
          src={tutorial7_1}
          width={363}
          height={54}
        />
        <img
          className={cn("max-w-67", DEFAULT_IMG_CLASS)}
          src={tutorial7_2}
          width={363}
          height={54}
        />
        <img
          className={cn("max-w-67", DEFAULT_IMG_CLASS)}
          src={tutorial7_3}
          width={363}
          height={54}
        />
        <div className="absolute w-12 border-4 -top-1 -bottom-1 right-9 border-red-600"></div>
      </div>
    ),
    TextContent: (
      <div className="flex flex-col gap-3">
        <p>This technique also applies when there are multiple numbers.</p>
        <p>
          For example, these are all of the ways 1 2 could fill in five squares.{" "}
          <strong>In every case, the fourth column would be colored-in</strong>,
          so you can fill that in.
        </p>
        <p>
          Those are the essentials of Pixellence! Consult the tutorials
          via the game menu for more detailed guidance.
        </p>
      </div>
    ),
  },
];
